import type { JSXChild, JSXProps } from 'engine/index';
import { Icon } from './Icon';
import { Copy } from 'lucide-static';

type InlineCodeProps = {
	clipboardButton?: boolean;
} & JSXProps;

export function InlineCode({ clipboardButton, ...props }: InlineCodeProps, ...children: JSXChild[]) {
	if (clipboardButton) {
		const view: HTMLElement = (
			<code
				{...props}
				class="inline-flex items-center gap-2 rounded-md border border-gray-200 bg-gray-100 pl-2 font-mono dark:border-gray-700 dark:bg-gray-900 dark:text-gray-300"
			>
				<span inline-code-content>{children}</span>
				<button
					copy-btn
					class="group relative rounded-md bg-gray-200 p-1 shadow-sm transition-all hover:bg-gray-300 active:bg-gray-100 dark:bg-gray-800 dark:hover:bg-gray-900 dark:active:bg-gray-700"
				>
					<Icon icon={Copy} class="pointer-events-none size-3 group-active:scale-95" />
					<span
						copy-btn-popup
						class="pointer-events-none absolute -top-1 left-1/2 block -translate-x-1/2 -translate-y-full whitespace-nowrap rounded-md border border-black bg-black/75 p-2 text-[10px] leading-none text-gray-200 opacity-0 shadow-md backdrop-blur-sm transition-opacity group-hover:opacity-100 dark:border-gray-500 dark:bg-gray-50/75 dark:text-gray-900"
					></span>
					<input type="text" class="hidden" />
				</button>
			</code>
		);
		const content = view.querySelector('[inline-code-content]') as HTMLElement;
		const copyBtn = view.querySelector('[copy-btn]') as HTMLElement;
		const copyBtnPopup = view.querySelector('[copy-btn-popup]') as HTMLElement;
		const input = view.querySelector('input') as HTMLInputElement;

		copyBtn.addEventListener('click', (e) => {
			input.value = content.innerText;
			input.select();
			input.setSelectionRange(0, 99999); // For mobile devices

			// Copy the text inside the text field
			navigator.clipboard.writeText(input.value);

			const fromW = copyBtnPopup.clientWidth;
			copyBtnPopup.innerText = 'Copied!';
			const toW = copyBtnPopup.clientWidth;
			copyBtnPopup.animate([{ width: fromW + 'px' }, { width: toW + 'px' }], {
				easing: 'ease-out',
				duration: 80,
			});
		});

		copyBtn.addEventListener('mouseover', (e) => {
			copyBtnPopup.innerText = 'Copy to clipboard';
		});

		return view;
	} else {
		return (
			<code
				{...props}
				class="rounded-md border border-gray-200 bg-gray-100 px-1 font-mono dark:border-gray-700 dark:bg-gray-900 dark:text-gray-300"
			>
				<span inline-code-content>{children}</span>
			</code>
		);
	}
}
